const defaultPatientQueryParams = {
  page: 1,
  size: 8,
  sort: "",
  order: "",
  search: "",
  advancedDob: "",
  advancedPhone: "",
  advancedHealthCard: "",
  advancedVersionCode: "",
  status: "active",
};

const initialState = {
  patients: {
    content: [],
    totalPages: 1,
  },
  patient: null,
  ...defaultPatientQueryParams,
  queryParams: { ...defaultPatientQueryParams },
  patient: null,
  loading: false,
  error: null,
  notepad: [],
  formsNotepad: [], // Separate state for forms-specific notepad data
  allergy: null,
  clinicalAlerts: null,
  lifestyle: null,
  medicationInitialData: null,
  lifestyleHistory: null,
  // Transient in-memory draft for patient creation (not persisted to storage)
  patientDraft: null,
};

const logPatientsDebug = () => {};

// Utility helpers to keep allergy slices and patient snapshot in sync
// Handles inconsistent API response formats (allergies/Allergies, noKnownAllergies/NoKnownAllergies)
const getAllergyList = (payload = {}) => {
  return payload?.allergies || payload?.Allergies || [];
};

// Detect whether the payload explicitly contained an allergies list (even if empty)
const hasAllergyList = (payload = {}) => {
  if (!payload || typeof payload !== 'object') return false;
  return Object.prototype.hasOwnProperty.call(payload, 'allergies') || Object.prototype.hasOwnProperty.call(payload, 'Allergies');
};

const getNoKnownAllergies = (payload, fallback) => {
  if (typeof payload?.noKnownAllergies === "boolean") return payload.noKnownAllergies;
  if (typeof payload?.NoKnownAllergies === "boolean") return payload.NoKnownAllergies;
  return fallback;
};

const normalizeAllergyPayload = (payload = {}, previous = {}) => {
  const payloadAllergies = getAllergyList(payload);
  const previousAllergies = getAllergyList(previous);
  const allergiesToUse = hasAllergyList(payload) ? payloadAllergies : previousAllergies;

  return {
    ...payload,
    allergies: allergiesToUse,
    noKnownAllergies: getNoKnownAllergies(payload, previous?.noKnownAllergies),
  };
};

const syncPatientAllergyInfo = (patient, allergyPayload) => {
  if (!patient) return patient;

  const nextAllergyInfo = normalizeAllergyPayload(
    allergyPayload,
    patient.allergyInfo
  );

  return {
    ...patient,
    allergyInfo: {
      ...(patient.allergyInfo || {}),
      ...nextAllergyInfo,
    },
  };
};

const syncAllergySlices = (state, payload, previous) => {
  const normalized = normalizeAllergyPayload(
    payload,
    previous ?? state.allergy ?? {}
  );
  return {
    ...state,
    allergy: normalized,
    patient: syncPatientAllergyInfo(state.patient, normalized),
    loading: false,
  };
};

const getMedicalConditionList = (payload = {}) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  
  if (payload?.medicalConditions) {
    if (Array.isArray(payload.medicalConditions)) {
      return payload.medicalConditions;
    }
    if (payload.medicalConditions.medicalConditions && Array.isArray(payload.medicalConditions.medicalConditions)) {
      return payload.medicalConditions.medicalConditions;
    }
  }
  
  if (payload?.MedicalConditions && Array.isArray(payload.MedicalConditions)) {
    return payload.MedicalConditions;
  }
  
  return [];
};

const syncPatientMedicalHistoryInfo = (patient, medicalConditionPayload) => {
  if (!patient) {
    return patient;
  }
  
  const medicalConditions = getMedicalConditionList(medicalConditionPayload);
  
  const normalizedConditions = medicalConditions.map((condition) => ({
    ...condition,
    name: condition.name || condition.conditionName || '',
    conditionName: condition.conditionName || condition.name || '',
  }));
  
  const existingMedicalHistoryInfo = patient.medicalHistoryInfo || {};
  
  return {
    ...patient,
    medicalHistoryInfo: {
      ...existingMedicalHistoryInfo,
      medicalConditions: [...normalizedConditions],
      medicalStudies: [...(existingMedicalHistoryInfo.medicalStudies || [])],
      surgeries: [...(existingMedicalHistoryInfo.surgeries || [])],
    },
  };
};

const syncMedicalConditionToPatient = (state, payload) => {
  return {
    ...state,
    patient: syncPatientMedicalHistoryInfo(state.patient, payload),
    loading: false,
  };
};

const patientsReducer = (state = initialState, action) => {
  switch (action.type) {
    case "GET_PATIENTS_SUCCESS":
      return {
        ...state,
        patients: action.payload,
        loading: false,
        error: null,
      };
    case "GET_PATIENTS_FAILURE":
      return { ...state, patients: [], loading: false, error: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_PATIENTS_QUERY_PARAMS":
      {
        const payload = Object.entries(action.payload || {}).reduce(
          (acc, [key, value]) => {
            if (value !== undefined) {
              acc[key] = value;
            }
            return acc;
          },
          {},
        );

        const nextQueryParams = {
          ...state.queryParams,
          ...payload,
        };

        const hasQueryParamChanges = Object.keys(payload).some(
          (key) => state.queryParams?.[key] !== nextQueryParams[key]
        );

        if (!hasQueryParamChanges) {
          logPatientsDebug("SET_PATIENTS_QUERY_PARAMS:no-op", {
            source: action?.meta?.source || "unknown",
            payload,
          });
          return state;
        }

        logPatientsDebug("SET_PATIENTS_QUERY_PARAMS:applied", {
          source: action?.meta?.source || "unknown",
          payload,
          nextQueryParams,
        });

        return {
          ...state,
          ...nextQueryParams,
          queryParams: nextQueryParams,
        };
      }
    case "CREATE_PATIENT_SUCCESS":
      return {
        ...state,
        patients: {
          ...state.patients,
          content: [action.payload.patient, ...state.patients.content],
        },
        patient: action.payload.patient,
        allergy: normalizeAllergyPayload(action.payload.patient?.allergyInfo || {}, state.allergy || {}),
        loading: false,
        error: null,
      };
    case "GET_PATIENT_DETAILS_SUCCESS":
      // Normalize allergy slice from returned patient details so UI doesn't show stale allergies
      return {
        ...state,
        patient: action.payload.patient,
        allergy: normalizeAllergyPayload(action.payload.patient?.allergyInfo || {}, state.allergy || {}),
        loading: false,
        error: null,
      };
    case "CREATE_PATIENT_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "DELETE_PATIENT_SUCCESS":
      return {
        ...state,
        patients: {
          ...state.patients,
          content: state.patients.content.filter(
            (patient) => patient.id !== action.payload
          ),
        },
        loading: false,
        error: null,
      };
    case "DELETE_PATIENT_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    
    case "GET_PATIENT_DETAILS_FAILURE":
      return {
        ...state,
        patient: null,
        loading: false,
        error: action.payload,
      };
    case "UPDATE_PATIENT_PERSONAL_DETAILS_SUCCESS":
      state.patient;
      action.payload;
      return {
        ...state,
        patient: { ...state.patient, ...action.payload },
        loading: false,
        error: null,
      };
    case "UPDATE_PATIENT_PERSONAL_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "UPDATE_PATIENT_CONTACT_DETAILS_SUCCESS":
      return {
        ...state,
        patient: { ...state.patient, contactInfo: { ...action.payload } },
        loading: false,
        error: null,
      };
    case "UPDATE_PATIENT_CONTACT_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "UPDATE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_SUCCESS":
      return {
        ...state,
        patient: {
          ...state.patient,
          provincialInsurance: { ...action.payload },
        },
        loading: false,
        error: null,
      };
    case "CREATE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_SUCCESS":
      return {
        ...state,
        patient: {
          ...state.patient,
          provincialInsurance: { ...action.payload },
        },
        loading: false,
        error: null,
      };
    case "UPDATE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "CREATE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "DELETE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_SUCCESS":
      return {
        ...state,
        patient: { ...state.patient, provincialInsurance: null },
        loading: false,
        error: null,
      };
    case "DELETE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "UPDATE_PATIENT_PRIVATE_INSURANCE_DETAILS_SUCCESS":
      let updatedThirdPartyInsurance = state.patient.thirdPartyInsurance.map(
        (insurance) => {
          if (insurance.id === action.payload.id) {
            return action.payload;
          }
          return insurance;
        }
      );
      return {
        ...state,
        patient: {
          ...state.patient,
          thirdPartyInsurance: updatedThirdPartyInsurance,
        },
        loading: false,
        error: null,
      };
    case "UPDATE_PATIENT_PRIVATE_INSURANCE_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "CREATE_PATIENT_PRIVATE_INSURANCE_DETAILS_SUCCESS":
      return {
        ...state,
        patient: {
          ...state.patient,
          thirdPartyInsurance: [
            ...state.patient.thirdPartyInsurance,
            action.payload,
          ],
        },
        loading: false,
        error: null,
      };
    case "CREATE_PATIENT_PRIVATE_INSURANCE_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "DELETE_PATIENT_PRIVATE_INSURANCE_DETAILS_SUCCESS":
      return {
        ...state,
        patient: {
          ...state.patient,
          thirdPartyInsurance: state.patient.thirdPartyInsurance.filter(
            (insurance) => insurance.id !== action.payload
          ),
        },
        loading: false,
        error: null,
      };
    case "DELETE_PATIENT_PRIVATE_INSURANCE_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "UPDATE_PATIENT_CAREGIVER_DETAILS_SUCCESS":
      return {
        ...state,
        patient: { ...state.patient, caregiver: action.payload },
        loading: false,
        error: null,
      };
    case "UPDATE_PATIENT_CAREGIVER_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "CREATE_PATIENT_CAREGIVER_DETAILS_SUCCESS":
      return {
        ...state,
        patient: {
          ...state.patient,
          caregiver: action.payload,
        },
        loading: false,
        error: null,
      };
    case "CREATE_PATIENT_CAREGIVER_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "DELETE_PATIENT_CAREGIVER_DETAILS_SUCCESS":
      return {
        ...state,
        patient: {
          ...state.patient,
          caregiver: state.patient.caregiver.filter(
            (caregiver) => caregiver.id !== action.payload
          ),
        },
        loading: false,
        error: null,
      };
    case "DELETE_PATIENT_CAREGIVER_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "CREATE_PATIENT_CAREPROVIDER_DETAILS_SUCCESS":
      return {
        ...state,
        patient: {
          ...state.patient,
          careProvider: [...state.patient.careProvider, action.payload],
        },
        loading: false,
        error: null,
      };
    case "CREATE_PATIENT_CAREPROVIDER_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "DELETE_PATIENT_CAREPROVIDER_DETAILS_SUCCESS":
      return {
        ...state,
        patient: {
          ...state.patient,
          careProvider: state.patient.careProvider.filter(
            (careProvider) => careProvider.id !== action.payload
          ),
        },
        loading: false,
        error: null,
      };
    case "DELETE_PATIENT_CAREPROVIDER_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "UPDATE_PATIENT_CAREPROVIDER_DETAILS_SUCCESS":
      let updatedCareProviders = state.patient.careProvider.map(
        (careProvider) => {
          if (careProvider.id === action.payload.id) {
            return action.payload;
          }
          return careProvider;
        }
      );
      return {
        ...state,
        patient: { ...state.patient, careProvider: updatedCareProviders },
        loading: false,
        error: null,
      };
    case "UPDATE_PATIENT_CAREPROVIDER_DETAILS_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "UPDATE_PATIENT_WEIGHT_SUCCESS":
      return {
        ...state,
        patient: { ...state.patient, bodyWeight: action.payload },
        loading: false,
        error: null,
      };
    case "UPDATE_PATIENT_WEIGHT_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "UPDATE_PATIENT_HEIGHT_SUCCESS":
      return {
        ...state,
        patient: { ...state.patient, bodyHeight: action.payload },
        loading: false,
        error: null,
      };
    case "UPDATE_PATIENT_HEIGHT_FAILURE":
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case "FETCH_NOTES_SUCCESS":
      const existingNotesMap = new Map(state.notepad.map(note => [note.id, note.createdAt]));
      const normalizedNotes = Array.isArray(action.payload) 
        ? action.payload.map(note => {
            const existingCreatedAt = existingNotesMap.get(note.id);
            return {
              ...note,
              createdAt: note.createdAt || note.CreatedAt || note.dateCreated || note.DateCreated || existingCreatedAt || new Date().toISOString(),
            };
          })
        : [];
      
      return {
        ...state,
        notepad: normalizedNotes,
        loading: false,
      };
    case "FETCH_NOTES_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "CREATE_NOTE_SUCCESS":
      const newNote = {
        ...action.payload,
        createdAt: action.payload.createdAt || action.payload.CreatedAt || new Date().toISOString(),
      };
      return {
        ...state,
        notepad: [...state.notepad, newNote],
        loading: false,
      };
    case "CREATE_NOTE_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "UPDATE_NOTE_SUCCESS":
      let notes = [...state.notepad];
      let updatedNoteIndex = notes.findIndex((i) => i.id === action.payload.id);
      const existingNote = notes[updatedNoteIndex];
      const updatedNote = {
        ...action.payload,
        createdAt: action.payload.createdAt || action.payload.CreatedAt || existingNote?.createdAt || new Date().toISOString(),
      };
      notes[updatedNoteIndex] = updatedNote;
      return {
        ...state,
        notepad: notes,
        loading: false,
      };
    case "UPDATE_NOTE_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "DELETE_NOTE_SUCCESS":
      return {
        ...state,
        notepad: state.notepad.filter((x) => x.id !== action.payload),
        loading: false,
      };
    case "DELETE_NOTE_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    // Forms-specific notepad actions
    case "FETCH_FORMS_NOTES_SUCCESS":
      return {
        ...state,
        formsNotepad: [...action.payload],
        loading: false,
      };
    case "FETCH_FORMS_NOTES_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "FETCH_ALLERGIES_SUCCESS":
      return syncAllergySlices(state, action.payload);
    case "FETCH_ALLERGIES_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "CREATE_ALLERGY_SUCCESS":
      return syncAllergySlices(state, action.payload);
    case "CREATE_ALLERGY_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "UPDATE_ALLERGY_SUCCESS":
      const currentUpdateAllergyState = state.allergy || {};
      const updatedAllergies = getAllergyList(currentUpdateAllergyState).map(
        (allergy) => (allergy.id === action.payload.id ? action.payload : allergy)
      );
      return syncAllergySlices(
        state,
        { ...currentUpdateAllergyState, allergies: updatedAllergies },
        currentUpdateAllergyState
      );
    case "DELETE_ALLERGY_SUCCESS":
      const currentDeleteAllergyState = state.allergy || {};
      const remainingAllergies = getAllergyList(currentDeleteAllergyState).filter(
        (allergy) => allergy.id !== action.payload
      );
      return syncAllergySlices(
        state,
        { ...currentDeleteAllergyState, allergies: remainingAllergies },
        currentDeleteAllergyState
      );
    case "UPDATE_ALLERGY_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "FETCH_MEDICAL_CONDITIONS_SUCCESS":
      return syncMedicalConditionToPatient(state, action.payload);
    case "CREATE_MEDICAL_CONDITION_SUCCESS":
      return syncMedicalConditionToPatient(state, action.payload);
    case "UPDATE_MEDICAL_CONDITION_SUCCESS":
      return syncMedicalConditionToPatient(state, action.payload);
    case "UPDATE_MEDICAL_CONDITION_SUCCESS_FROM_CHART":
      const currentConditions = getMedicalConditionList(state.patient?.medicalHistoryInfo || {});
      const updatedConditions = currentConditions.map(
        (c) => (c.id === action.payload.id ? action.payload : c)
      );
      return syncMedicalConditionToPatient(state, { medicalConditions: updatedConditions });
    case "DELETE_MEDICAL_CONDITION_SUCCESS":
      const existingConditions = getMedicalConditionList(state.patient?.medicalHistoryInfo || {});
      const remainingConditions = existingConditions.filter((c) => c.id !== action.payload);
      return syncMedicalConditionToPatient(state, { medicalConditions: remainingConditions });
    case "CREATE_MEDICAL_HISTORY_SUCCESS":
      return syncMedicalConditionToPatient(state, action.payload);
    case "UPDATE_MEDICAL_HISTORY_SUCCESS":
      return syncMedicalConditionToPatient(state, action.payload);
    case "FETCH_MEDICAL_CONDITIONS_FAILURE":
    case "CREATE_MEDICAL_CONDITION_FAILURE":
    case "UPDATE_MEDICAL_CONDITION_FAILURE":
    case "DELETE_MEDICAL_CONDITION_FAILURE":
      return { ...state, error: action.payload, loading: false };
    case "FETCH_CLINICAL_ALERTS_SUCCESS":
      return {
        ...state,
        clinicalAlerts: { ...action.payload },
        loading: false,
      };
    case "FETCH_CLINICAL_ALERTS_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "CREATE_CLINICAL_ALERTS_SUCCESS":
      return {
        ...state,
        clinicalAlerts: { ...action.payload },
        loading: false,
      };
    case "CREATE_CLINICAL_ALERTS_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "UPDATE_CLINICAL_ALERT_SUCCESS":
      // Handle both ClinicalAlerts (capital C) and clinicalAlerts (lowercase c) formats
      const alertsArray = state.clinicalAlerts?.ClinicalAlerts || state.clinicalAlerts?.clinicalAlerts || [];
      if (alertsArray.length > 0) {
        const updatedClinicalAlertIndex = alertsArray.findIndex(
          (alert) => alert.id === action.payload.id
        );
        const updatedClinicalAlerts = [...alertsArray];
        if (updatedClinicalAlertIndex !== -1) {
          // Merge the payload with existing alert to preserve all fields including diagnosisDate
          updatedClinicalAlerts[updatedClinicalAlertIndex] = {
            ...updatedClinicalAlerts[updatedClinicalAlertIndex],
            ...action.payload,
            // Ensure diagnosisDate is preserved
            diagnosisDate: action.payload.diagnosisDate !== undefined ? action.payload.diagnosisDate : updatedClinicalAlerts[updatedClinicalAlertIndex].diagnosisDate,
            DiagnosisDate: action.payload.DiagnosisDate !== undefined ? action.payload.DiagnosisDate : updatedClinicalAlerts[updatedClinicalAlertIndex].DiagnosisDate,
          };
        } else {
          // If not found, add it (shouldn't happen, but handle edge case)
          updatedClinicalAlerts.push(action.payload);
        }
        
        // Update the appropriate format
        const key = state.clinicalAlerts?.ClinicalAlerts ? 'ClinicalAlerts' : 'clinicalAlerts';
        return {
          ...state,
          clinicalAlerts: {
            ...state.clinicalAlerts,
            [key]: updatedClinicalAlerts,
          },
          loading: false,
        };
      }
      return { ...state, loading: false };
    case "UPDATE_CLINICAL_ALERT_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "DELETE_CLINICAL_ALERTS_SUCCESS":
      if (state.clinicalAlerts?.ClinicalAlerts) {
        const filteredClinicalAlerts = state.clinicalAlerts.ClinicalAlerts.filter(
          (alert) => !action.payload.includes(alert.id)
        );
        return {
          ...state,
          clinicalAlerts: {
            ...state.clinicalAlerts,
            ClinicalAlerts: filteredClinicalAlerts,
          },
          loading: false,
        };
      }
      return { ...state, loading: false };
    case "DELETE_CLINICAL_ALERTS_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "FETCH_LIFESTYLE_SUCCESS":
      return {
        ...state,
        lifestyle: { ...action.payload },
        loading: false,
      };
    case "FETCH_LIFESTYLE_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "CREATE_LIFESTYLE_SUCCESS":
      return {
        ...state,
        lifestyle: { ...action.payload },
        loading: false,
      };
    case "CREATE_LIFESTYLE_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "FETCH_LIFESTYLE_HISTORY_SUCCESS":
      return {
        ...state,
        lifestyleHistory: action.payload.content || action.payload, // Support both paginated and non-paginated responses
        lifestyleHistoryPagination: {
          currentPage: action.payload.currentPage || 1,
          totalPages: action.payload.totalPages || 1,
          totalRecords: action.payload.totalRecords || (Array.isArray(action.payload) ? action.payload.length : 0),
        },
        loading: false,
        error: null, // Clear any previous errors on success
      };
    case "FETCH_LIFESTYLE_HISTORY_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "FETCH_LIFESTYLE_TRENDS_SUCCESS":
      return {
        ...state,
        lifestyleTrends: action.payload,
        loading: false,
      };
    case "FETCH_LIFESTYLE_TRENDS_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "SET_PATIENT_DRAFT":
      return {
        ...state,
        patientDraft: action.payload,
      };
    case "CLEAR_PATIENT_DRAFT":
      return {
        ...state,
        patientDraft: null,
      };
    case "UPDATE_LIFESTYLE_SUCCESS":
      return {
        ...state,
        lifestyle: { ...action.payload },
        loading: false,
      };
    case "UPDATE_LIFESTYLE_FAILURE":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case "SET_MEDICATION_INITIAL_DATA":
      return {
        ...state,
        medicationInitialData: action.payload,
      };
    case "CLEAR_MEDICATION_INITIAL_DATA":
      return {
        ...state,
        medicationInitialData: null,
      };
    default:
      return state;
  }
};

export default patientsReducer;
